/**
 * Operate-UI client for the narrate endpoint that powers Feature #3's
 * "Ask about this data" affordance. Single-shot request/response —
 * markdown is small enough that streaming would add complexity
 * without much UX win.
 */
import { AxiosError } from "axios";
import { api } from "./api";

export interface NarrateResult {
  /** Markdown analysis from the LLM. */
  markdown:     string;
  /** Number of rows the model actually saw (capped server-side). */
  rowsAnalyzed: number;
  /** Total rows in the cached dataset; differs from rowsAnalyzed only when truncated. */
  rowsTotal:    number;
}

export interface NarrateApiError {
  code:   string;
  detail: string;
  status: number;
}

export async function narrateOverData(
  intent: string,
  columnNames: string[],
  rows: Array<Array<unknown>>,
): Promise<NarrateResult> {
  try {
    const { data } = await api.post<{
      markdown?:     string;
      rowsAnalyzed?: number;
      rowsTotal?:    number;
    }>("/visualize/narrate", {
      intent,
      dataset: { columns: columnNames, rows },
    });
    return {
      markdown:     typeof data?.markdown     === "string" ? data.markdown : "",
      rowsAnalyzed: typeof data?.rowsAnalyzed === "number" ? data.rowsAnalyzed : 0,
      rowsTotal:    typeof data?.rowsTotal    === "number" ? data.rowsTotal    : rows.length,
    };
  } catch (e) {
    if (e instanceof AxiosError) {
      const body = e.response?.data as { error?: string; detail?: string } | undefined;
      const err: NarrateApiError = {
        code:   typeof body?.error  === "string" ? body.error  : "http.status",
        detail: typeof body?.detail === "string" ? body.detail : (e.message ?? `HTTP ${e.response?.status}`),
        status: e.response?.status ?? 0,
      };
      throw err;
    }
    throw e;
  }
}
